import { ChevronDown, ChevronRight, Locate, SearchX, TriangleAlert } from 'lucide-react';
import { useRef, useState } from 'react';
import {
    Button,
    Empty,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    cn,
} from 'shared-ui';

import type { NetworkTableAreaRow, NetworkTableDmaRow, NetworkTablePoiRow } from '@/api/networkTableApi';
import type { EntityMapFilterState } from '@/components/map/utils/entityMapFilters';
import type { PoiFeatureProperties } from '@/components/map/utils/poiFeatureProperties';

import { STAT_PLACEHOLDER, formatVolume } from '@/utils/formatStat';

import { PoiRowActions } from './PoiRowActions';
import { PoiSummaryGrid, UnavailableValue } from './PoiSummaryGrid';
import { areaLocateTarget, dmaLocateTarget } from './locateTarget';
import { COLUMN_HEADING_CLASS, SortableColumnHead, type ColumnDef } from './SortableColumnHead';
import { useTableScrollChrome, type TableHeaderChrome } from './useTableScrollChrome';
import {
    areaLabel,
    dmaLabel,
    useNetworkTable,
    type NetworkTableRow,
    type NetworkTableSortColumn,
} from './useNetworkTable';

interface NetworkTablePanelProps {
    clientId: string | null;
    /** When false, the table makes no requests (it is closed). */
    enabled: boolean;
    /** The map's committed filters, or null when inactive. Never the draft. */
    filters: EntityMapFilterState | null;
    /** The committed (debounced) search term; empty string when not searching. */
    search: string;
    /** Copy and Show Details are maximised-only; Locate is available in both open layouts. */
    isMaximised: boolean;
    /**
     * Gates the phone presentation (three columns, no sorting, reduced indents, expanded-only POI
     * actions, scroll chrome). Threaded as a prop rather than read from useMediaQuery here, so the
     * region holds the single media subscription and tests control the mode explicitly. Defaults to
     * the desktop presentation.
     */
    isMobile?: boolean;
    /** Actions the map owns. See NetworkTableRegionProps for the full contract. */
    actions: NetworkTableActions;
    /**
     * Renders the panel's header, receiving the server's filtered record total.
     *
     * The header shows that total beneath the title, but the total is owned here (it comes back with
     * the root request). Passing the header down keeps one source of truth: the alternative — the
     * region holding its own copy, synced up from this component — would duplicate the state and
     * need an effect to maintain it.
     *
     * The second argument carries the scroll container's chrome state (owned here, where the
     * container lives) for the mobile header's shadow and search-block retraction. Optional so
     * desktop call sites and tests can ignore it.
     */
    renderHeader?: (totalFilteredPoiCount: number, chrome?: TableHeaderChrome) => React.ReactNode;
}

/**
 * The columns drawn for a layout. Reference is maximised-only: in the 50/50 split the table is half
 * the viewport, and that column was the width that pushed the trailing actions cell — the only way
 * to Locate a row — behind a horizontal scrollbar. Only DMA rows populate it, so hiding it costs
 * split mode nothing it cannot recover by maximising.
 *
 * A phone gets exactly three columns — the hierarchy name and the two figures that matter most —
 * per the design. Note that mobile is always maximised (the split is unreachable below the
 * breakpoint), so without its own branch a phone would inherit the full maximised set.
 */
function visibleColumns(isMaximised: boolean, isMobile: boolean): ColumnDef[] {
    if (isMobile) {
        return COLUMNS.filter(
            (column) => column.key === 'name' || column.key === 'leaksFound' || column.key === 'potentialSaving',
        );
    }
    return isMaximised ? COLUMNS : COLUMNS.filter((column) => column.key !== 'reference');
}

/**
 * The visible columns plus, on desktop, the trailing actions column — every full-row colSpan.
 * Mobile renders no actions column at all: Area/DMA Locate is desktop-only, and the POI actions
 * live inline on the expanded POI row.
 */
function columnCount(isMaximised: boolean, isMobile: boolean): number {
    return visibleColumns(isMaximised, isMobile).length + (isMobile ? 0 : 1);
}

/**
 * Mobile heading cells: fixed compact widths so the name column takes every remaining pixel under
 * the fixed table layout, with the figures aligned as the design asks — Leaks centred, Saving
 * flush right so its values form a clean right-hand edge. The headings may wrap (the cells default
 * to nowrap); the values below never do.
 */
const MOBILE_HEAD_CLASS: Partial<Record<NetworkTableSortColumn, string>> = {
    leaksFound: 'w-16 text-center whitespace-normal',
    // Wide enough for "Pot. Saving" (held together by its no-break space) on one line, so the
    // heading sits on two lines at most: measure, then unit.
    potentialSaving: 'w-28 text-right whitespace-normal',
};

/**
 * Row hover: `--accent` background (#DFECFE light / #404040 dark) with Origin Blue text, overriding
 * shared-ui TableRow's `hover:bg-muted/50`. Both come from tokens, so no `dark:` variant is needed.
 *
 * The text colour is INHERITED down into the cells rather than applied to each one. That is what
 * makes it recolour exactly the text that is `--foreground` today — the black/white names and
 * figures — while content with a colour of its own (muted secondary text, status badges, the blue
 * action icons) keeps it. Anything that must follow the row despite declaring its own colour has to
 * opt in with `group-hover` (see SummaryFact).
 */
const ROW_HOVER_CLASS = 'hover:bg-accent hover:text-origin-blue';

/**
 * Every expandable row (Area, DMA and the POI row) must deal with shared-ui TableRow's
 * `has-aria-expanded:bg-muted/50`, which tints any row containing an `aria-expanded="true"` element
 * — i.e. every expanded row. Both classes below are load-bearing:
 *
 * - `has-aria-expanded:bg-transparent` drops the muted tint. Passing the same variant + property
 *   makes tailwind-merge strip shared-ui's class, so it never reaches the DOM. Expansion is already
 *   signalled by the chevron and the revealed content, so the tint carries nothing.
 * - `has-aria-expanded:hover:bg-accent` puts the hover back. Without it an expanded row has NO hover
 *   at all: `:has([aria-expanded=true])` and `:hover` are both specificity (0,2,0), both land in
 *   `@layer utilities`, and Tailwind emits the `has-*` variant later — so it wins on source order and
 *   paints straight over the hover. Stacking the variants raises this to (0,3,0), which wins on
 *   specificity and therefore does not depend on emission order.
 */
const GROUP_ROW_CLASS = cn(ROW_HOVER_CLASS, 'has-aria-expanded:bg-transparent has-aria-expanded:hover:bg-accent');

/**
 * The tint marking the POI row that Locate has highlighted. The `has-aria-expanded` twin restates it
 * for the expanded row, replacing GROUP_ROW_CLASS's transparent via tailwind-merge — otherwise
 * expanding a located row would erase its highlight. Hover still beats both at (0,3,0).
 */
const LOCATED_ROW_CLASS = 'bg-secondary/60 has-aria-expanded:bg-secondary/60';

/**
 * Reference and the aggregate figures — muted at rest, matching the POI summary grid's values, so a
 * value reads the same wherever it appears. The name column is excluded: it is the row's identity,
 * not a value, and stays at full contrast.
 *
 * The `group-hover` is what keeps them in step with the rest of the row on hover. Declaring a colour
 * at rest blocks the row's inherited Origin Blue hover, so these cells have to opt back into it
 * explicitly — at (0,2,0) it beats the muted colour's (0,1,0) without needing `!important`.
 */
const VALUE_CELL_CLASS = 'text-muted-foreground group-hover:text-origin-blue';

/**
 * The map-facing actions the table triggers. Locate NEVER opens a panel or a stats card — it
 * navigates and highlights only. Show Details is the sole path from the table into PoiDetailsPanel.
 */
export interface NetworkTableActions {
    onLocatePoi: (poi: NetworkTablePoiRow) => void;
    onLocateArea: (area: NetworkTableAreaRow) => void;
    onLocateDma: (dma: NetworkTableDmaRow) => void;
    onShowPoiDetails: (poi: PoiFeatureProperties, coords: [number, number]) => void;
    /** Id of the POI currently highlighted from the table, so its row can show as active. */
    locatedPoiId: string | null;
}

/**
 * The six columns, each sortable through its heading's menu. One active sort applies at whichever
 * levels the column is meaningful for — the per-level field mapping (and the absence of a POI
 * Potential Saving sort) lives in useNetworkTable.
 */
const COLUMNS: ColumnDef[] = [
    { key: 'name', label: 'Area / DMA / POI', numeric: false },
    // Only DMAs carry a reference (their ident) — confirmed. An Area is identified by its name, and
    // a POI by its Worklist and Stop; neither has a reference, so neither fills this column.
    { key: 'reference', label: 'Reference', numeric: false },
    { key: 'dmaCount', label: "DMA's", numeric: true },
    { key: 'poiCount', label: "POI's", numeric: true },
    { key: 'leaksFound', label: 'Leaks Found', numeric: true },
    // The mobile label joins "Pot." to "Saving" with an INVISIBLE no-break space (U+00A0, not a
    // plain space — check your editor before retyping it). That leaves the heading exactly one
    // place to wrap — before the unit — so it can never spill onto a third line however the
    // font renders.
    { key: 'potentialSaving', label: 'Potential Saving (Ml/D)', mobileLabel: 'Pot. Saving (Ml/D)', numeric: true },
];

/**
 * Formats a potential-saving value into Ml/D, or a placeholder when absent.
 *
 * The server sends litres/day (the StatsService measure). `formatVolume` is the same conversion the
 * stats bar and the map selection cards apply to this very measure, so one number never reads
 * differently on two surfaces.
 *
 * The unit repeats on every populated row rather than living only in the heading: the column is read
 * row by row, and a bare number here invites being taken for litres.
 */
function formatSaving(value: number | null): string {
    if (value == null) return STAT_PLACEHOLDER;
    return `${formatVolume(value)} Ml/D`;
}

export function NetworkTablePanel({
    clientId,
    enabled,
    filters,
    search,
    isMaximised,
    isMobile = false,
    actions,
    renderHeader,
}: NetworkTablePanelProps) {
    const {
        rows,
        status,
        totalFilteredPoiCount,
        sort,
        applySort,
        retryRoot,
        toggleArea,
        toggleDma,
        loadMoreDmas,
        loadMorePois,
        retryDmas,
        retryPois,
    } = useNetworkTable({ clientId, enabled, filters, search });

    // `rows` is empty while a new context loads (the hook withholds superseded data), so the
    // skeleton — not stale rows — covers every reload, not just the first.
    const isLoading = status === 'loading';

    // The scroll chrome lives with the scroll container it watches. Desktop attaches nothing.
    const scrollRef = useRef<HTMLDivElement>(null);
    const chrome = useTableScrollChrome(scrollRef, isMobile && enabled);

    return (
        // `relative` anchors the bottom overflow shadow to the panel's bottom edge, which is also
        // the scroll container's bottom edge — the strip therefore sits directly above the fixed
        // mobile footer without touching the scroll container itself.
        <div className="relative flex min-h-0 flex-1 flex-col">
            {renderHeader?.(totalFilteredPoiCount, { atTop: chrome.atTop, direction: chrome.direction })}

            {/* network-table-scroll recolours both scrollbars to the sidebar-border token (index.css). */}
            <div ref={scrollRef} className="network-table-scroll min-h-0 flex-1 overflow-auto">
                {/* Fixed layout on mobile: the heading widths size the columns, the name column
                    absorbs the rest and truncates — nothing can force a horizontal scroll.
                    network-table-mobile (index.css) redraws the row dividers inset from the
                    viewport edges and airs out the heading cells, per the mobile design. */}
                <Table className={cn(isMobile && 'network-table-mobile table-fixed')}>
                    <TableHeader className="bg-background sticky top-0 z-10">
                        <TableRow>
                            {visibleColumns(isMaximised, isMobile).map((column) =>
                                isMobile ? (
                                    // Sorting is a desktop affordance: on a phone the heading is
                                    // inert text — no button, no icon, no aria-sort.
                                    <TableHead key={column.key} className={MOBILE_HEAD_CLASS[column.key]}>
                                        <span className={COLUMN_HEADING_CLASS}>
                                            {column.mobileLabel ?? column.label}
                                        </span>
                                    </TableHead>
                                ) : (
                                    <SortableColumnHead
                                        key={column.key}
                                        column={column}
                                        sort={sort}
                                        onApplySort={applySort}
                                    />
                                ),
                            )}
                            {/* Actions column: no header text, but a real th so the row actions
                                align to the right edge on every row type. Absent on mobile, where
                                no collapsed row carries actions. */}
                            {!isMobile && (
                                <TableHead className="w-0">
                                    <span className="sr-only">Actions</span>
                                </TableHead>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading && <TableSkeleton isMaximised={isMaximised} isMobile={isMobile} />}

                        {status === 'error' && (
                            <StatusMessageRow colSpan={columnCount(isMaximised, isMobile)}>
                                <RootErrorState onRetry={retryRoot} />
                            </StatusMessageRow>
                        )}

                        {status === 'loaded' && rows.length === 0 && (
                            <StatusMessageRow colSpan={columnCount(isMaximised, isMobile)}>
                                <RootEmptyState hasSearch={search !== ''} hasFilters={filters !== null} />
                            </StatusMessageRow>
                        )}

                        {rows.map((row) => (
                            <NetworkRow
                                key={row.key}
                                row={row}
                                isMaximised={isMaximised}
                                isMobile={isMobile}
                                actions={actions}
                                onToggleArea={toggleArea}
                                onToggleDma={toggleDma}
                                onRetryDmas={retryDmas}
                                onRetryPois={retryPois}
                                onLoadMoreDmas={loadMoreDmas}
                                onLoadMorePois={loadMorePois}
                            />
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* The "more below" affordance: shown only while content actually continues past the
                viewport, gone at the bottom and when nothing overflows. Its strength follows the
                StatsBar scroll-shadow idiom. */}
            {isMobile && chrome.hasMoreBelow && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-4 [box-shadow:inset_0_-16px_12px_-12px_rgba(0,0,0,0.12)] dark:[box-shadow:inset_0_-16px_12px_-12px_rgba(0,0,0,0.5)]"
                />
            )}
        </div>
    );
}

function StatusMessageRow({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} className="text-muted-foreground py-8 text-center text-sm">
                {children}
            </TableCell>
        </TableRow>
    );
}

/** Table-aligned placeholder rows. Uses the portal's existing skeleton idiom (see StatItem). */
function TableSkeleton({ isMaximised, isMobile }: { isMaximised: boolean; isMobile: boolean }) {
    return (
        <>
            {Array.from({ length: 6 }, (_, rowIndex) => (
                <TableRow key={rowIndex} aria-hidden>
                    {visibleColumns(isMaximised, isMobile).map((column) => (
                        <TableCell key={column.key}>
                            <div
                                className={cn(
                                    'bg-muted h-4 animate-pulse rounded',
                                    column.numeric ? 'w-12' : 'w-40',
                                    // The placeholder bars can outsize a phone's fixed columns;
                                    // capping them keeps the skeleton inside the real grid.
                                    isMobile && 'max-w-full',
                                )}
                            />
                        </TableCell>
                    ))}
                    {/* Keeps the placeholder aligned with the real rows' actions column. */}
                    {!isMobile && <TableCell className="w-0" />}
                </TableRow>
            ))}
            <TableRow>
                <TableCell colSpan={columnCount(isMaximised, isMobile)} className="sr-only" aria-live="polite">
                    Loading network records…
                </TableCell>
            </TableRow>
        </>
    );
}

/**
 * The three empty cases are worded distinctly: a user who has filtered or searched needs to know
 * their own criteria caused the emptiness, not that the network has no records.
 */
function RootEmptyState({ hasSearch, hasFilters }: { hasSearch: boolean; hasFilters: boolean }) {
    // Search is the narrowest and most recent action, so it wins when both are active.
    const { title, description } = hasSearch
        ? {
              title: 'No Network Records match your search',
              description:
                  'Search looks across Area, DMA, worklist, what3words and stop number, within the active filters.',
          }
        : hasFilters
          ? {
                title: 'No Network Records match the current filters',
                description: 'Adjust or reset the filters to see more of the network.',
            }
          : {
                title: 'No Network Records',
                description: 'There are no records to show for this client yet.',
            };

    return (
        <Empty className="border-none py-4">
            <EmptyHeader>
                <EmptyMedia variant="icon">
                    <SearchX />
                </EmptyMedia>
                <EmptyTitle>{title}</EmptyTitle>
                <EmptyDescription>{description}</EmptyDescription>
            </EmptyHeader>
        </Empty>
    );
}

function RootErrorState({ onRetry }: { onRetry: () => void }) {
    return (
        <Empty className="border-none py-4">
            <EmptyHeader>
                <EmptyMedia variant="icon">
                    <TriangleAlert />
                </EmptyMedia>
                <EmptyTitle>Could not load network records</EmptyTitle>
                <EmptyDescription>Something went wrong fetching the network hierarchy.</EmptyDescription>
            </EmptyHeader>
            <Button variant="outline" size="sm" onClick={onRetry}>
                Retry
            </Button>
        </Empty>
    );
}

interface NetworkRowProps {
    row: NetworkTableRow;
    isMaximised: boolean;
    isMobile: boolean;
    actions: NetworkTableActions;
    onToggleArea: (area: NetworkTableAreaRow) => void;
    onToggleDma: (area: NetworkTableAreaRow, dma: NetworkTableDmaRow) => void;
    onRetryDmas: (area: NetworkTableAreaRow) => void;
    onRetryPois: (area: NetworkTableAreaRow, dma: NetworkTableDmaRow) => void;
    onLoadMoreDmas: (area: NetworkTableAreaRow) => void;
    onLoadMorePois: (area: NetworkTableAreaRow, dma: NetworkTableDmaRow) => void;
}

function NetworkRow({
    row,
    isMaximised,
    isMobile,
    actions,
    onToggleArea,
    onToggleDma,
    onRetryDmas,
    onRetryPois,
    onLoadMoreDmas,
    onLoadMorePois,
}: NetworkRowProps) {
    switch (row.kind) {
        case 'area':
            return (
                <AreaTableRow
                    area={row.area}
                    expanded={row.expanded}
                    // Mobile is always maximised, so without the second condition a phone
                    // would draw the Reference column its three-column layout excludes.
                    showReference={isMaximised && !isMobile}
                    isMobile={isMobile}
                    onToggle={() => {
                        onToggleArea(row.area);
                    }}
                    onLocate={actions.onLocateArea}
                />
            );
        case 'dma':
            return (
                <DmaTableRow
                    dma={row.dma}
                    expanded={row.expanded}
                    showReference={isMaximised && !isMobile}
                    isMobile={isMobile}
                    onToggle={() => {
                        onToggleDma(row.area, row.dma);
                    }}
                    onLocate={actions.onLocateDma}
                />
            );
        case 'poi':
            return (
                <PoiTableRow
                    poi={row.poi}
                    area={row.area}
                    dma={row.dma}
                    isMaximised={isMaximised}
                    isMobile={isMobile}
                    located={actions.locatedPoiId === (row.poi.mapEntityId ?? row.poi.id)}
                    onLocate={actions.onLocatePoi}
                    onShowDetails={actions.onShowPoiDetails}
                />
            );
        case 'status':
            return (
                <ChildStatusRow
                    level={row.level}
                    state={row.state}
                    colSpan={columnCount(isMaximised, isMobile)}
                    isMobile={isMobile}
                    onRetry={() => {
                        if (row.dma) {
                            onRetryPois(row.area, row.dma);
                        } else {
                            onRetryDmas(row.area);
                        }
                    }}
                />
            );
        case 'loadMore':
            return (
                <LoadMoreRow
                    level={row.level}
                    nextCount={row.nextCount}
                    parentLabel={row.parentLabel}
                    state={row.state}
                    colSpan={columnCount(isMaximised, isMobile)}
                    isMobile={isMobile}
                    onLoadMore={() => {
                        if (row.dma) {
                            onLoadMorePois(row.area, row.dma);
                        } else {
                            onLoadMoreDmas(row.area);
                        }
                    }}
                />
            );
    }
}

/**
 * The hierarchy indent for a child level. A phone keeps the same visual ordering — Area, then a
 * step for DMA, then one more for POI — but with much smaller steps, spending the recovered width
 * on the names themselves. Desktop keeps its established indents untouched.
 */
function childIndent(level: 'dma' | 'poi', isMobile: boolean): string {
    if (level === 'poi') return isMobile ? 'pl-6' : 'pl-16';
    return isMobile ? 'pl-3' : 'pl-8';
}

/** "10 more DMAs in Alpha" — the next batch, not the total remaining. */
function loadMoreLabel(level: 'dma' | 'poi', nextCount: number, parentLabel: string): string {
    const noun = level === 'dma' ? 'DMA' : 'POI';
    const plural = nextCount === 1 ? noun : `${noun}s`;
    return `${nextCount.toLocaleString()} more ${plural} in ${parentLabel}`;
}

function LoadMoreRow({
    level,
    nextCount,
    parentLabel,
    state,
    colSpan,
    isMobile,
    onLoadMore,
}: {
    level: 'dma' | 'poi';
    nextCount: number;
    parentLabel: string;
    state: 'idle' | 'loading' | 'error';
    colSpan: number;
    isMobile: boolean;
    onLoadMore: () => void;
}) {
    const indent = childIndent(level, isMobile);
    return (
        <TableRow>
            <TableCell colSpan={colSpan} className="py-2 text-sm">
                <div className={cn('flex items-center gap-2', indent)}>
                    {state === 'error' && <span className="text-muted-foreground">Could not load more.</span>}
                    <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        disabled={state === 'loading'}
                        onClick={onLoadMore}
                    >
                        {state === 'loading' && <Spinner className="size-4" />}
                        {state === 'error' ? 'Retry' : loadMoreLabel(level, nextCount, parentLabel)}
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    );
}

function ExpandToggle({
    expanded,
    label,
    onClick,
    className,
}: {
    expanded: boolean;
    label: string;
    onClick: () => void;
    /** Merged last — the POI row's top-aligned layout needs a vertical nudge the centred rows don't. */
    className?: string;
}) {
    return (
        <Button
            variant="ghost"
            size="icon"
            // The ghost variant paints an open (aria-expanded) button bg-muted, which held a grey
            // square behind the open chevron and masked the row's own hover colour. Transparent
            // lets the row show through, so the open control reads exactly like the closed one.
            className={cn('size-6 shrink-0 aria-expanded:bg-transparent', className)}
            aria-expanded={expanded}
            aria-label={label}
            onClick={onClick}
        >
            {/* size PROP as well as class: absoluteStrokeWidth divides by the prop, so without it
                the CSS downscale thins the stroke to 1px instead of the design's true 1.5px. */}
            {expanded ? (
                <ChevronDown className="text-origin-blue size-4" size={16} strokeWidth={1.5} absoluteStrokeWidth />
            ) : (
                <ChevronRight className="text-origin-blue size-4" size={16} strokeWidth={1.5} absoluteStrokeWidth />
            )}
        </Button>
    );
}

/**
 * The trailing actions cell. Area and DMA rows carry only Locate; POI rows span the table and lay
 * their own actions out inline, but land in the same right-hand position.
 */
function ActionsCell({ children }: { children: React.ReactNode }) {
    return (
        <TableCell className="w-0">
            <div className="flex items-center justify-end">{children}</div>
        </TableCell>
    );
}

/**
 * Locate for an Area or DMA row. Disabled when the group has no extent to fly to — an Unassigned
 * bucket whose POIs carry no coordinates. Never opens the stats card.
 */
function GroupLocateButton({
    label,
    target,
    onLocate,
}: {
    label: string;
    target: ReturnType<typeof areaLocateTarget>;
    onLocate: () => void;
}) {
    const disabled = target === null;
    return (
        <Tooltip>
            <TooltipTrigger
                render={
                    <Button
                        variant="ghost"
                        size="icon"
                        // hover:bg-accent replaces the ghost variant's muted grey (the shared
                        // accent-hover rule never lands here — TooltipTrigger overwrites the
                        // Button's data-slot). The dark twin is needed too: the variant's
                        // dark:hover:bg-muted/50 sits on a different variant stack, so
                        // tailwind-merge keeps it unless restated.
                        className="group/action hover:bg-accent dark:hover:bg-accent size-7 shrink-0"
                        aria-label={`Locate ${label} on the map`}
                        disabled={disabled}
                        onClick={onLocate}
                    >
                        {/* Explicit colour: TooltipTrigger overwrites the Button's data-slot, so
                            shared-ui's ghost-button Origin Blue rule does not reach this icon.
                            Hover deepens it to Origin Blue Secondary, per the design. */}
                        <Locate
                            className="text-origin-blue group-hover/action:text-origin-blue-secondary size-4"
                            size={16}
                            strokeWidth={1.5}
                            absoluteStrokeWidth
                        />
                    </Button>
                }
            />
            {/* Same wording as the POI row's Locate, which is the same action one level down. */}
            <TooltipContent side="top">{disabled ? 'No mapped records to locate' : 'Locate on Map'}</TooltipContent>
        </Tooltip>
    );
}

function AreaTableRow({
    area,
    expanded,
    showReference,
    isMobile,
    onToggle,
    onLocate,
}: {
    area: NetworkTableAreaRow;
    expanded: boolean;
    showReference: boolean;
    isMobile: boolean;
    onToggle: () => void;
    onLocate: (area: NetworkTableAreaRow) => void;
}) {
    // areaLabel, not a local ternary: the unassigned-group WORDING is still an open decision
    // ('Unassigned' / 'Unknown' / 'No DMA'), and the Load More rows already read it from there.
    // Two copies would resolve differently the day it changes.
    const name = areaLabel(area);
    // An expanded Area keeps the hover colour while open, marking the branch the visible children
    // belong to. The row class handles the inherited text (the name); the value cells declare their
    // own muted colour, so each opts into the override below — the same shape as their group-hover.
    const expandedClass = expanded && 'text-origin-blue';
    return (
        <TableRow className={cn(GROUP_ROW_CLASS, 'group', expandedClass)}>
            <TableCell>
                {/* On mobile the name yields to the fixed figure columns: it truncates (the full
                    text stays in the accessibility tree) rather than pushing them off screen. */}
                <div className={cn('flex items-center gap-2', isMobile && 'min-w-0')}>
                    <ExpandToggle
                        expanded={expanded}
                        label={`${expanded ? 'Collapse' : 'Expand'} ${name}`}
                        onClick={onToggle}
                    />
                    <span
                        className={cn(
                            // Medium, not semibold: enough to mark the top of the hierarchy
                            // against the plain-weight DMA names without reading as heavy — the
                            // same weight the POI identity values and record-count line use.
                            'font-medium',
                            isMobile && 'min-w-0 truncate',
                            area.isUnassigned && 'text-muted-foreground italic',
                        )}
                    >
                        {name}
                    </span>
                </div>
            </TableCell>
            {/* Areas have no reference — their name is their identity (the table PK is the name). */}
            {showReference && <TableCell />}
            {!isMobile && (
                <TableCell className={cn(VALUE_CELL_CLASS, 'tabular-nums', expandedClass)}>
                    {area.dmaCount.toLocaleString()}
                </TableCell>
            )}
            {!isMobile && (
                <TableCell className={cn(VALUE_CELL_CLASS, 'tabular-nums', expandedClass)}>
                    {area.poiCount.toLocaleString()}
                </TableCell>
            )}
            <TableCell className={cn(VALUE_CELL_CLASS, 'tabular-nums', expandedClass, isMobile && 'text-center')}>
                {area.leaksFound.toLocaleString()}
            </TableCell>
            <TableCell className={cn(VALUE_CELL_CLASS, 'tabular-nums', expandedClass, isMobile && 'text-right')}>
                {formatSaving(area.potentialSaving)}
            </TableCell>
            {/* No actions on mobile: Locate for a group is a desktop affordance — the phone's
                three-column design carries no trailing column, and POI Locate remains one level
                down on the expanded row. */}
            {!isMobile && (
                <ActionsCell>
                    <GroupLocateButton
                        label={name}
                        target={areaLocateTarget(area)}
                        onLocate={() => {
                            onLocate(area);
                        }}
                    />
                </ActionsCell>
            )}
        </TableRow>
    );
}

function DmaTableRow({
    dma,
    expanded,
    showReference,
    isMobile,
    onToggle,
    onLocate,
}: {
    dma: NetworkTableDmaRow;
    expanded: boolean;
    showReference: boolean;
    isMobile: boolean;
    onToggle: () => void;
    onLocate: (dma: NetworkTableDmaRow) => void;
}) {
    // `label` identifies the row (Locate, the expander's accessible name): the ident is the DMA's
    // canonical, unique identity, so it stays the handle even though the name is what is drawn here.
    // Via dmaLabel so the unassigned wording has one definition — see AreaTableRow.
    const label = dmaLabel(dma);
    // The name column carries the DMA's name alone, its ident having moved to Reference. A named
    // DMA is the norm; falling back to the ident keeps a nameless one identifiable rather than blank.
    const displayName = dma.isUnassigned ? label : (dma.name ?? dma.ident ?? '');
    // Same open-branch treatment as an expanded Area: the row and its values hold the hover colour
    // while the DMA's POIs are on show beneath it.
    const expandedClass = expanded && 'text-origin-blue';
    return (
        <TableRow className={cn(GROUP_ROW_CLASS, 'group', expandedClass)}>
            <TableCell>
                <div className={cn('flex items-center gap-2', childIndent('dma', isMobile), isMobile && 'min-w-0')}>
                    <ExpandToggle
                        expanded={expanded}
                        label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
                        onClick={onToggle}
                    />
                    <span
                        className={cn(
                            isMobile && 'min-w-0 truncate',
                            dma.isUnassigned && 'text-muted-foreground italic',
                        )}
                    >
                        {displayName}
                    </span>
                </div>
            </TableCell>
            {/* UnavailableValue, not a bare em dash: a dash has no accessible name, so this cell
                would read as empty rather than as "no reference". */}
            {showReference && (
                <TableCell className={cn(VALUE_CELL_CLASS, expandedClass)}>
                    {dma.isUnassigned ? <UnavailableValue /> : dma.ident}
                </TableCell>
            )}
            {!isMobile && <TableCell />}
            {!isMobile && (
                <TableCell className={cn(VALUE_CELL_CLASS, 'tabular-nums', expandedClass)}>
                    {dma.poiCount.toLocaleString()}
                </TableCell>
            )}
            <TableCell className={cn(VALUE_CELL_CLASS, 'tabular-nums', expandedClass, isMobile && 'text-center')}>
                {dma.leaksFound.toLocaleString()}
            </TableCell>
            <TableCell className={cn(VALUE_CELL_CLASS, 'tabular-nums', expandedClass, isMobile && 'text-right')}>
                {formatSaving(dma.potentialSaving)}
            </TableCell>
            {/* Desktop-only for the same reason as the Area row's actions. */}
            {!isMobile && (
                <ActionsCell>
                    <GroupLocateButton
                        label={label}
                        target={dmaLocateTarget(dma)}
                        onLocate={() => {
                            onLocate(dma);
                        }}
                    />
                </ActionsCell>
            )}
        </TableRow>
    );
}

/**
 * The POI row's accessible name — "Point of Interest, Worklist 20260505NI02, Stop 40".
 *
 * The header shows Worklist and Stop under explicit labels but drops the "Point of Interest" prefix,
 * which said nothing the hierarchy did not already say and crowded the line in split mode. The level
 * is kept for screen readers here and in the row's visually-hidden prefix, so nothing is lost by not
 * drawing it.
 */
function poiAccessibleName(poi: NetworkTablePoiRow): string {
    return `Point of Interest, Worklist ${poi.worklist}, Stop ${String(poi.stop)}`;
}

/**
 * A POI row: an identifying line that is always shown, with the detail facts folded away behind an
 * expander. POI rows have no aggregate cells, so the row spans the table and lays itself out inline.
 *
 * Expansion is local state, unlike the Area and DMA levels which keep theirs in useNetworkTable.
 * That difference is deliberate: expanding an Area or a DMA issues a request, so its state has to
 * live with the request lifecycle (in-flight guards, context resets, search auto-expansion). A POI's
 * facts are already in the row it was rendered from, so expanding one is pure presentation and owns
 * nothing the hook needs to know about. The trade-off is that expansion resets if the row unmounts —
 * collapsing its parent, or changing page/sort/filters — which is the right default anyway: a new
 * context should not restore detail the user opened against the old one.
 */
function PoiTableRow({
    poi,
    area,
    dma,
    isMaximised,
    isMobile,
    located,
    onLocate,
    onShowDetails,
}: {
    poi: NetworkTablePoiRow;
    area: NetworkTableAreaRow;
    dma: NetworkTableDmaRow;
    isMaximised: boolean;
    isMobile: boolean;
    located: boolean;
    onLocate: (poi: NetworkTablePoiRow) => void;
    onShowDetails: (poi: PoiFeatureProperties, coords: [number, number]) => void;
}) {
    const [expanded, setExpanded] = useState(false);
    const accessibleName = poiAccessibleName(poi);

    return (
        <TableRow className={cn(GROUP_ROW_CLASS, 'group', located && LOCATED_ROW_CLASS)}>
            {/*
             * `whitespace-normal` is load-bearing: shared-ui's TableCell sets `whitespace-nowrap`,
             * and `white-space` INHERITS, so every descendant would refuse to wrap. `break-words`
             * cannot override it — overflow-wrap only acts where lines are allowed to break. The old
             * row never noticed because it truncated; this one wraps, so the nowrap has to go.
             */}
            <TableCell colSpan={columnCount(isMaximised, isMobile)} className="whitespace-normal">
                <div className={childIndent('poi', isMobile)}>
                    {/* The actions stay on the identifying line, so they hold the same place
                        whether the row is collapsed or expanded. `items-start` keeps the chevron and
                        the actions on the first line when the identity wraps in split mode. */}
                    {/* items-start pins the controls to the first line when the identity wraps —
                        but it also top-aligns three different heights (20px text line, 24px
                        expander, 28px actions), leaving the text visually high. The top margins
                        on the chevron and the text push both DOWN onto the 28px actions' centre
                        (14px): the tallest element stays put, so the line stays vertically centred
                        in the row, while a wrapped identity still keeps the controls on its first
                        line. Nudging the controls up instead re-broke centring — it shrank the
                        layout box at the top and floated the whole line above the row's middle. */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-2">
                            <ExpandToggle
                                expanded={expanded}
                                label={`${expanded ? 'Collapse' : 'Expand'} ${accessibleName}`}
                                className="mt-0.5"
                                onClick={() => {
                                    setExpanded((value) => !value);
                                }}
                            />
                            {/* The collapsed line carries identity ALONE — status lives only in the
                                expanded facts (the Assessment Result field), per the design. It
                                wraps rather than truncates, so a long Worklist folds onto a second
                                line instead of running under the actions. At rest it declares no
                                colour, so it reads in the row's foreground and follows the row's
                                hover colour like the rest of the row's text; while expanded it
                                holds Origin Blue — the same open-branch marker as an expanded
                                Area or DMA row, scoped to this line so the summary grid beneath
                                keeps its own colours. */}
                            <span className={cn('mt-1 min-w-0 break-words', expanded && 'text-origin-blue')}>
                                {/* The level the visual design drops, kept for screen readers. */}
                                <span className="sr-only">Point of Interest, </span>
                                <PoiIdentityFact label="Worklist" value={poi.worklist} />
                                <span aria-hidden="true">{' · '}</span>
                                <PoiIdentityFact label="Stop" value={String(poi.stop)} />
                            </span>
                        </div>
                        {/* A phone's collapsed rows carry no actions and reserve no space for them,
                            per the design: the actions appear on this identity line only while the
                            POI is expanded. Desktop keeps them always on show. */}
                        {(!isMobile || expanded) && (
                            <div className="shrink-0">
                                <PoiRowActions
                                    poi={poi}
                                    area={area}
                                    dma={dma}
                                    isMaximised={isMaximised}
                                    onLocate={onLocate}
                                    onShowDetails={onShowDetails}
                                />
                            </div>
                        )}
                    </div>
                    {expanded && (
                        <PoiSummaryGrid poi={poi} area={area} dma={dma} isMaximised={isMaximised} isMobile={isMobile} />
                    )}
                </div>
            </TableCell>
        </TableRow>
    );
}

/**
 * A labelled part of the POI's identity — "Worklist 20260505NI02".
 *
 * The label is drawn, not implied: a bare "20260505NI02 - 40" gave no clue what either number was.
 * Rendered inline (not as a block) so the two facts and the separator reflow as one sentence.
 *
 * No colour of its own: the identity line renders in one colour end to end, inherited from the
 * row (theme foreground at rest, the hover colour under the pointer), with the value picked out
 * by weight alone.
 */
function PoiIdentityFact({ label, value }: { label: string; value: string }) {
    return (
        <span>
            <span>{label} </span>
            <span className="font-medium">{value}</span>
        </span>
    );
}

function ChildStatusRow({
    level,
    state,
    colSpan,
    isMobile,
    onRetry,
}: {
    level: 'dma' | 'poi';
    state: 'loading' | 'error' | 'empty';
    colSpan: number;
    isMobile: boolean;
    onRetry: () => void;
}) {
    const indent = childIndent(level, isMobile);
    return (
        <TableRow>
            <TableCell colSpan={colSpan} className="text-muted-foreground py-2 text-sm">
                <div className={cn('flex items-center gap-2', indent)}>
                    {state === 'loading' && (
                        <>
                            <Spinner className="size-4" /> Loading…
                        </>
                    )}
                    {state === 'empty' && <span>No records.</span>}
                    {state === 'error' && (
                        <>
                            <span>Could not load records.</span>
                            <Button variant="link" size="sm" className="h-auto p-0" onClick={onRetry}>
                                Retry
                            </Button>
                        </>
                    )}
                </div>
            </TableCell>
        </TableRow>
    );
}
